import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { Address } from '@app/domain/model/Address';
import { useCallback, useMemo, useState, ChangeEvent } from 'react';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { HomeInsuranceRequestResult } from '@app/service/eshop/home/actions/requestHomeInsurance';
import { formatLithuanianPhone } from '@app/lib/phone/formatLithuanianPhone';

export interface PolicyholderFormFields {
    firstName: string;
    lastName: string;
    address: Nullable<Address>;
    phoneNumber: string;
    email: string;
    emailConfirmation: string;
}

export interface PolicyholderFormHandlers {
    handleFirstNameChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleLastNameChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleAddressChange: (address: Nullable<Address>) => void;
    handlePhoneNumberChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleEmailChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleEmailConfirmationChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

export function usePolicyholderFormFields(authState: AuthState, requestResult: Nullable<HomeInsuranceRequestResult>) {
    const selectedClient = authState.selectedClient;
    const profile = authState.profile;

    const initialFields = useMemo<PolicyholderFormFields>(() => {
        const defaultAddress = requestResult?.address ?? profile?.mainAddress ?? profile?.addresses?.[0] ?? null;

        return {
            firstName: selectedClient?.firstName ?? profile?.firstName ?? '',
            lastName: selectedClient?.lastName ?? profile?.name ?? '',
            address: defaultAddress,
            phoneNumber: (profile?.mainPhone ?? '').replace(/^\+370/, ''),
            email: profile?.mainEmail ?? '',
            emailConfirmation: '',
        };
    }, [
        profile?.addresses,
        profile?.firstName,
        profile?.mainAddress,
        profile?.mainEmail,
        profile?.mainPhone,
        profile?.name,
        requestResult?.address,
        selectedClient?.firstName,
        selectedClient?.lastName,
    ]);

    const [fields, setFields] = useState<PolicyholderFormFields>(initialFields);

    useChangeEffect(() => {
        const profileAddress = profile?.mainAddress ?? profile?.addresses?.[0] ?? null;
        setFields(prevFields => ({
            ...prevFields,
            firstName: selectedClient?.firstName ?? profile?.firstName ?? prevFields.firstName,
            lastName: selectedClient?.lastName ?? profile?.name ?? prevFields.lastName,
            address: prevFields.address ?? profileAddress,
            phoneNumber: (profile?.mainPhone ?? '').replace(/^\+370/, '') || prevFields.phoneNumber,
            email: profile?.mainEmail ?? prevFields.email,
        }));
    }, profile);

    const setDefaultAddress = useCallback((address: Nullable<Address>) => {
        setFields(prevFields => ({ ...prevFields, address }));
    }, []);

    const handleFirstNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setFields(prevFields => ({ ...prevFields, firstName: e.target.value }));
    }, []);

    const handleLastNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setFields(prevFields => ({ ...prevFields, lastName: e.target.value }));
    }, []);

    const handleAddressChange = useCallback((address: Nullable<Address>) => {
        setFields(prevFields => ({ ...prevFields, address }));
    }, []);

    const handlePhoneNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const formatted = formatLithuanianPhone(e.target.value);
        setFields(prevFields => ({ ...prevFields, phoneNumber: formatted }));
    }, []);

    const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setFields(prevFields => ({ ...prevFields, email: e.target.value }));
    }, []);

    const handleEmailConfirmationChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setFields(prevFields => ({ ...prevFields, emailConfirmation: e.target.value }));
    }, []);

    const handlers: PolicyholderFormHandlers = useMemo(
        () => ({
            handleFirstNameChange,
            handleLastNameChange,
            handleAddressChange,
            handlePhoneNumberChange,
            handleEmailChange,
            handleEmailConfirmationChange,
        }),
        [
            handleFirstNameChange,
            handleLastNameChange,
            handleAddressChange,
            handlePhoneNumberChange,
            handleEmailChange,
            handleEmailConfirmationChange,
        ],
    );

    return {
        fields,
        handlers,
        setFields,
        setDefaultAddress,
    };
}
